"use client"

/**
 * MobileTooltip - Touch-aware tooltip for dashboard pages
 *
 * Problem: Radix UI Tooltip fires pointerenter → pointerleave instantly on
 * mobile tap, causing a flash-open-then-close effect.
 *
 * Solution: On touch devices, use controlled open state that toggles on tap
 * and closes on outside tap. On desktop, delegate entirely to Radix's native
 * hover behavior (zero changes to desktop UX).
 *
 * Usage: Drop-in replacement for Tooltip/TooltipTrigger/TooltipContent from
 * "@/components/ui/tooltip". Same API, mobile-safe.
 */

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Touch detection hook (matches pointer: coarse media query)
// ---------------------------------------------------------------------------
function useIsTouchDevice() {
    const [isTouch, setIsTouch] = React.useState(false)

    React.useEffect(() => {
        const mq = window.matchMedia("(pointer: coarse)")
        const update = () => setIsTouch(mq.matches)
        update()
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    return isTouch
}

// ---------------------------------------------------------------------------
// Context to pass touch state + controlled open from Root → Trigger
// ---------------------------------------------------------------------------
interface MobileTooltipCtx {
    isTouch: boolean
    open: boolean
    setOpen: (v: boolean) => void
    triggerRef: React.RefObject<HTMLElement | null>
}

const Ctx = React.createContext<MobileTooltipCtx | null>(null)

// ---------------------------------------------------------------------------
// MobileTooltip (Root)
// ---------------------------------------------------------------------------
interface MobileTooltipProps {
    children: React.ReactNode
    /** Radix delay for desktop hover (default 200) */
    delayDuration?: number
    /** Radix disableHoverableContent passthrough */
    disableHoverableContent?: boolean
}

function MobileTooltip({
    children,
    delayDuration = 200,
    disableHoverableContent,
}: MobileTooltipProps) {
    const isTouch = useIsTouchDevice()
    const [open, setOpen] = React.useState(false)
    const triggerRef = React.useRef<HTMLElement | null>(null)

    // Close on outside tap (touch only)
    React.useEffect(() => {
        if (!isTouch || !open) return

        const handlePointerDown = (e: PointerEvent) => {
            if (e.pointerType !== "touch") return
            if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }

        // Small delay so the current tap doesn't immediately close
        const raf = requestAnimationFrame(() => {
            document.addEventListener("pointerdown", handlePointerDown)
        })

        return () => {
            cancelAnimationFrame(raf)
            document.removeEventListener("pointerdown", handlePointerDown)
        }
    }, [isTouch, open])

    // On touch: fully controlled (open prop). On desktop: let Radix manage.
    const rootProps = isTouch
        ? { open, onOpenChange: setOpen, delayDuration: 0, disableHoverableContent: true }
        : { delayDuration, disableHoverableContent }

    return (
        <Ctx.Provider value={{ isTouch, open, setOpen, triggerRef }}>
            <TooltipPrimitive.Root {...rootProps}>
                {children}
            </TooltipPrimitive.Root>
        </Ctx.Provider>
    )
}

// ---------------------------------------------------------------------------
// MobileTooltipTrigger
// ---------------------------------------------------------------------------
interface MobileTooltipTriggerProps
    extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger> {}

const MobileTooltipTrigger = React.forwardRef<
    HTMLButtonElement,
    MobileTooltipTriggerProps
>(({ onClick, ...props }, ref) => {
    const ctx = React.useContext(Ctx)

    // Merge refs so we can track the trigger element for outside-click detection
    const internalRef = React.useRef<HTMLButtonElement>(null)
    const mergedRef = React.useCallback(
        (node: HTMLButtonElement | null) => {
            internalRef.current = node
            if (ctx) (ctx.triggerRef as React.MutableRefObject<HTMLElement | null>).current = node
            if (typeof ref === "function") ref(node)
            else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
        },
        [ref, ctx]
    )

    // Track whether a touch just happened to prevent double-toggle from click
    const touchHandledRef = React.useRef(false)

    const handleClick = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            // If touch already handled this interaction, skip
            if (touchHandledRef.current) {
                touchHandledRef.current = false
                return
            }
            onClick?.(e)
        },
        [onClick]
    )

    const handleTouchEnd = React.useCallback(
        (e: React.TouchEvent<HTMLButtonElement>) => {
            if (ctx?.isTouch) {
                // Prevent the subsequent mouse/click events that cause flash
                e.preventDefault()
                touchHandledRef.current = true
                ctx.setOpen(!ctx.open)
                // Reset flag after a tick in case click doesn't fire
                setTimeout(() => { touchHandledRef.current = false }, 300)
            }
        },
        [ctx]
    )

    return (
        <TooltipPrimitive.Trigger
            ref={mergedRef}
            onTouchEnd={handleTouchEnd}
            onClick={handleClick}
            {...props}
        />
    )
})
MobileTooltipTrigger.displayName = "MobileTooltipTrigger"

// ---------------------------------------------------------------------------
// MobileTooltipContent (same styling as base TooltipContent)
// ---------------------------------------------------------------------------
const MobileTooltipContent = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
        variant?: "dark" | "light"
    }
>(({ className, sideOffset = 6, variant = "dark", ...props }, ref) => (
    <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                "z-50 overflow-hidden rounded-lg px-3 py-2 text-[13px] leading-relaxed shadow-lg",
                variant === "dark" && "bg-gray-900 text-white [&_*]:text-white",
                variant === "light" &&
                    "bg-white text-gray-900 border border-gray-200/80 shadow-xl shadow-black/10",
                "animate-in fade-in-0 zoom-in-95",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
                "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
                "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                className
            )}
            {...props}
        />
    </TooltipPrimitive.Portal>
))
MobileTooltipContent.displayName = "MobileTooltipContent"

// Re-export Provider as-is
const MobileTooltipProvider = TooltipPrimitive.Provider

export {
    MobileTooltip,
    MobileTooltipTrigger,
    MobileTooltipContent,
    MobileTooltipProvider,
}
